import { useState, useEffect, useMemo } from "react";
import {
  Box,
  FormControlLabel,
  Grid2,
  List,
  ListItem,
  Popover,
  Typography,
  styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { PrimaryFormActionButton } from "../../../Components/StyledComponent";
import FilterSelectBox from "../../Overview/components/FilterSelectBox";
import SearchInput from "../../Overview/components/SearchInput";
import CustomCheckbox from "../../Overview/components/Checkbox";

const popoverPaperStyles = {
  py: "20px",
  px: "16px",
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  boxShadow:
    "2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)",
};

const StyledListItem = styled(ListItem)(() => ({
  paddingLeft: 0,
  paddingTop: 2,
  paddingBottom: 2,
}));

const getDisplayLabel = (selected, all) => {
  if (!selected || selected.length === 0 || selected.length === all.length) return "All";
  if (selected.length === 1) return selected[0];
  return `${selected[0]} +${selected.length - 1}`;
};

const FlatMultiSelectFilter = ({ label, options = [], selectedValues, onChange }) => {
  const { t } = useTranslation(["common"]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [internalSelected, setInternalSelected] = useState(() => [...options]);
  const [search, setSearch] = useState("");
  const [displayLabel, setDisplayLabel] = useState("All");

  const open = Boolean(anchorEl);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  }, [options, search]);

  const isAllSelected = internalSelected.length === options.length;
  const isNoneSelected = internalSelected.length === 0;

  const toggleOption = (option) => {
    setInternalSelected((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const handleApply = () => {
    const allSelected = internalSelected.length === options.length;
    onChange(allSelected ? [] : [...internalSelected]);
    setDisplayLabel(getDisplayLabel(internalSelected, options));
    setAnchorEl(null);
  };

  // Sync internal state when popover opens
  useEffect(() => {
    if (open) {
      setInternalSelected(
        selectedValues && selectedValues.length > 0 ? [...selectedValues] : [...options]
      );
      setSearch("");
    }
  }, [open]);

  // Sync display label when selectedValues change externally
  useEffect(() => {
    setDisplayLabel(getDisplayLabel(selectedValues, options));
  }, [selectedValues, options]);

  // Reset when options change (e.g. loaded from API)
  useEffect(() => {
    setInternalSelected([...options]);
    setDisplayLabel("All");
  }, [options]);

  return (
    <>
      <FilterSelectBox
        open={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        label={label}
        displayValue={displayLabel}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: popoverPaperStyles } }}
      >
        <Grid2 container flexDirection="column" gap={2} sx={{ width: "280px" }}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="fw700s14lh24c3D4B5A">{label}</Typography>
            <Typography
              variant="fw500s12lh24c6D72F6"
              sx={{
                cursor: isAllSelected ? "not-allowed" : "pointer",
                color: isAllSelected ? "#8F8F8F" : "#6D72F6",
              }}
              onClick={() => !isAllSelected && setInternalSelected([...options])}
            >
              {t("actions.selectAll")}
            </Typography>
          </Box>

          {/* Search */}
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${label}`}
          />

          {/* Options list */}
          <Box sx={{ height: "200px", overflowY: "auto" }}>
            <List disablePadding>
              {filteredOptions.map((option) => (
                <StyledListItem key={option} dense>
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <CustomCheckbox
                        checked={internalSelected.includes(option)}
                        onChange={() => toggleOption(option)}
                      />
                    }
                    label={
                      <Typography variant="fw400s12c494949" sx={{ lineHeight: "24px" }}>
                        {option}
                      </Typography>
                    }
                  />
                </StyledListItem>
              ))}
            </List>
          </Box>

          {/* Footer */}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5 }}
          >
            <Typography
              variant="fw500s12lh24c6D72F6"
              sx={{
                cursor: isNoneSelected ? "not-allowed" : "pointer",
                color: isNoneSelected ? "#8F8F8F" : "#6D72F6",
              }}
              onClick={() => !isNoneSelected && setInternalSelected([])}
            >
              {t("actions.deselectAll")}
            </Typography>
            <PrimaryFormActionButton onClick={handleApply} disabled={isNoneSelected}>
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Grid2>
      </Popover>
    </>
  );
};

export default FlatMultiSelectFilter;
